import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  useMediaQuery,
  useTheme,
  Typography
} from '@mui/material';

interface Column {
  id: string;
  label: string;
  render: (lead: any) => React.ReactNode;
  minWidth?: number; 
  sortable?: boolean;
  sortFn?: (a: any, b: any) => number;
}

interface LeadsTableProps {
  leads: any[];
  columns: Column[];
  totalProfiles: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  selectedUsernames?: string[];
  onSelectLead?: (username: string, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
  showCheckbox?: boolean;
}

const LeadsTable: React.FC<LeadsTableProps> = ({
  leads,
  columns,
  totalProfiles,
  currentPage,
  setCurrentPage,
  selectedUsernames = [],
  onSelectLead,
  onSelectAll,
  showCheckbox = true,
}) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery('(min-width:1300px)');
  const isMediumScreen = useMediaQuery('(min-width:900px)');
  const totalPages = Math.ceil(totalProfiles / 10);

  const [sortState, setSortState] = useState<{ id: string; direction: 'asc' | 'desc' } | null>(null);

  const sortedLeads = [...leads];
  if (sortState) {
    const col = columns.find(c => c.id === sortState.id);
    if (col?.sortable) {
      if (col.sortFn) {
        sortedLeads.sort((a, b) =>
          sortState.direction === 'asc' ? col.sortFn!(a, b) : col.sortFn!(b, a)
        );
      } else {
        sortedLeads.sort((a, b) => {
          const valA = a[sortState.id];
          const valB = b[sortState.id];
          if (typeof valA === 'number' && typeof valB === 'number') {
            return sortState.direction === 'asc' ? valA - valB : valB - valA;
          }
          return 0;
        });
      }
    }
  }

  const handleSortClick = (column: Column) => {
    if (!column.sortable) return;
    if (sortState?.id === column.id) {
      setSortState({
        id: column.id,
        direction: sortState.direction === 'asc' ? 'desc' : 'asc',
      });
    } else {
      setSortState({ id: column.id, direction: 'asc' });
    }
  };

  return (
    <TableContainer 
      component={Paper} 
      elevation={0} 
      className="table-responsive border rounded"
    >
      <Table className="table table-sm mb-0">
        <TableHead>
          <TableRow>
            {showCheckbox && onSelectAll && (
              <TableCell 
                className="align-middle text-nowrap px-2 py-2"
              >
                <Checkbox
                  indeterminate={selectedUsernames.length > 0 && selectedUsernames.length < leads.length}
                  checked={leads.length > 0 && selectedUsernames.length === leads.length}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  size="small"
                  sx={{
                    borderRadius: '4px',
                    border: '1px solid #D0D5DD',
                    boxShadow: 'none',
                    width: 16,
                    height: 16,
                    p: 0,
                    color: '#5175E0',
                    '&.Mui-checked': {
                      color: '#5175E0',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: 16,
                    }
                  }}
                />
              </TableCell>
            )}
            {columns.map((column) => {
              const isSortedColumn = sortState?.id === column.id;
              return (
                <TableCell 
                  key={column.id}
                  className="align-middle text-nowrap px-2 py-2 small"
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      onClick={() => handleSortClick(column)}
                      className="btn btn-link p-0 d-flex align-items-center gap-1 text-decoration-none text-secondary small fw-semibold"
                    >
                      {column.label}
                      <span>{isSortedColumn ? (sortState!.direction === 'asc' ? '▲' : '▼') : ''}</span>
                    </button>
                  ) : (
                    column.label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        {leads.length === 0 ? (
           <TableBody>
           <TableRow>
             <td colSpan={columns.length + (showCheckbox ? 1 : 0)} className="text-center py-4 bg-light text-primary fw-medium">
               No record found
             </td>
           </TableRow>
         </TableBody>
        ) : (

          <TableBody>
          {sortedLeads.map((lead, index) => (
            <TableRow
              key={lead.username || index}
              className={`${
                selectedUsernames.includes(lead.username)
                  ? "bg-success-subtle border-success border border-2"
                  : index % 2 === 0
                  ? "bg-white"
                  : "bg-light"
              }`}
            >
              {showCheckbox && onSelectLead && (
                <TableCell 
                  className="align-middle text-nowr ap px-2 py-2 small"
                >
                  <Checkbox
                    checked={selectedUsernames.includes(lead.username)}
                    onChange={(e) => onSelectLead(lead.username, e.target.checked)}
                    size="small"
                    sx={{
                      borderRadius: '4px',
                      border: '1px solid #D0D5DD',
                      boxShadow: 'none',
                      width: 16,
                      height: 16,
                      p: 0,
                      color: '#5175E0',
                      '&.Mui-checked': {
                        color: '#5175E0',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: 16,
                      }
                    }}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell 
                key={column.id}
                className="align-middle text-nowrap px-2 py-2 small"
                >
                  {column.render(lead)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        )}
      </Table>
      {totalProfiles > 10 && (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-3 py-2">
          <div className="d-flex gap-3">
            <button
              className="btn btn-outline-secondary btn-sm rounded-pill px-3 py-1"
              onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <button
              className="btn btn-outline-secondary btn-sm rounded-pill px-3 py-1"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage * 10 >= totalProfiles}
            >
              Next
            </button>
          </div>
          <div className="small text-muted">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      )}
    </TableContainer>
  );
};

export default LeadsTable;